import { useEffect, useMemo, useState } from "react";
import { Calculator, Pin, X } from "lucide-react";

const KEY = (id) => `calcnotes:${id}`;

function evaluate(raw) {
  if (!raw || !raw.trim()) return { ok: false, value: "" };
  const expr = raw.replace(/×/g, "*").replace(/÷/g, "/").replace(/,/g, ".").replace(/\s+/g, "");
  if (!/^[0-9+\-*/%.()]+$/.test(expr)) return { ok: false, value: "Geçersiz" };
  try {
    // percentage as /100 when standalone like 45% -> (45/100)
    const safe = expr.replace(/(\d+(?:\.\d+)?)%/g, "($1/100)");
    // eslint-disable-next-line no-new-func
    const v = Function(`"use strict";return (${safe})`)();
    if (typeof v !== "number" || !isFinite(v)) return { ok: false, value: "Hata" };
    return { ok: true, value: Math.round(v * 10000) / 10000 };
  } catch {
    return { ok: false, value: "Hata" };
  }
}

export default function CalcBar({ projectId }) {
  const [expr, setExpr] = useState("");
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    try { setNotes(JSON.parse(localStorage.getItem(KEY(projectId)) || "[]")); } catch { setNotes([]); }
  }, [projectId]);

  const persist = (next) => {
    setNotes(next);
    try { localStorage.setItem(KEY(projectId), JSON.stringify(next)); } catch (_) {}
  };

  const res = useMemo(() => evaluate(expr), [expr]);

  const addNote = () => {
    if (!res.ok || !expr.trim()) return;
    persist([{ id: Date.now().toString(), expr: expr.trim(), value: res.value }, ...notes].slice(0, 30));
    setExpr("");
  };
  const removeNote = (nid) => persist(notes.filter((n) => n.id !== nid));

  return (
    <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 px-4 md:px-8 py-2.5" data-testid="calc-bar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 shrink-0">
          <Calculator className="w-4 h-4 text-amber-600" /> Hesap
        </span>
        <input
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
          placeholder="ör. 1200*0.045+8000  ·  Enter = Not Ekle"
          data-testid="calc-input"
          className="flex-1 min-w-[200px] rounded-md border border-slate-300 px-3 py-1.5 text-sm font-mono outline-none focus:ring-2 focus:ring-amber-500"
        />
        <span data-testid="calc-result" className={`min-w-[110px] text-right font-mono text-sm font-bold tabular-nums ${res.ok ? "text-emerald-700" : "text-slate-300"}`}>
          {expr.trim() ? (res.ok ? `= ${res.value.toLocaleString("tr-TR")}` : res.value) : "= "}
        </span>
        <button onClick={addNote} disabled={!res.ok} data-testid="calc-add-note"
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-40">
          <Pin className="w-4 h-4" /> Not Ekle
        </button>
      </div>
      {notes.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="calc-notes">
          {notes.map((n) => (
            <div key={n.id} data-testid={`calc-note-${n.id}`}
              className="group relative rounded-md bg-yellow-100 border border-yellow-300 px-3 py-1.5 shadow-sm">
              <div className="font-mono text-[11px] text-yellow-800/70 leading-tight">{n.expr}</div>
              <div className="font-mono text-sm font-bold text-yellow-900 tabular-nums leading-tight">{Number(n.value).toLocaleString("tr-TR")}</div>
              <button onClick={() => removeNote(n.id)} data-testid={`calc-note-del-${n.id}`}
                className="absolute -top-1.5 -right-1.5 hidden group-hover:flex w-4 h-4 rounded-full bg-yellow-600 text-white items-center justify-center">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
